import React from 'react';
import { observer } from 'mobx-react';
import { Link } from '@this/shared/ui/navigations/link';
import { Radio, RadioGroup } from '@this/src/components/shared/ui/inputs/radio';
import type { StationJson, RailwayAreaJson } from '@this/domain/station';
import type ReserveInfo from '@this/domain/reserve_info2';
import Transit from '@this/domain/transit/transit2';
import type ReservingTripItem from '@this/domain/trip/reserving_trip_item2';

export type PickupStationSelection = {
  searchQueryItemId: number;
  travelerIndex?: number | null;
  selectionValue: string;
  pickupStation: string;
  pickupStationId: number | null;
  pickupService: number | null;
  ticketingMethod: string | null;
};

type Props = {
  reserveInfo: ReserveInfo;
  stations: StationJson[];
  serviceId: number;
  travelerIndex?: number | null;
  onPickupStationChange: (selection: PickupStationSelection) => void;
};

type PickupStationNoticeProps = {
  perTraveler?: boolean;
};

const PICKUP_SERVICE_EKINET = 0;
const PICKUP_SERVICE_E5489 = 1;

const JR_EAST_URL = 'https://www.eki-net.com/top/jrticket/guide/uketori/';
const JR_WEST_URL =
  'https://www.jr-odekake.net/goyoyaku/e5489/reservation/?_gl=1*cebitu*_ga*MTI5NTA1Njg5Mi4xNzA5Mjg3MTQ5*_ga_1EX2KJB2NH*MTcxMDQyNzY1Mi40LjEuMTcxMDQyNzg0Ni42MC4wLjA.';

const sectionStyle: React.CSSProperties = {
  marginTop: '12px',
  marginBottom: '12px',
  padding: '12px 16px',
  backgroundColor: '#f5f5f5',
  borderRadius: '4px'
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '12px',
  marginBottom: '12px',
  color: '#333'
};

const routeLabelStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 'bold',
  marginBottom: '8px',
  color: '#333'
};

const itemSectionStyle: React.CSSProperties = {
  marginBottom: '16px'
};

const serviceSelectionSectionStyle: React.CSSProperties = {
  marginTop: '12px',
  padding: '10px',
  backgroundColor: '#fff',
  borderRadius: '4px'
};

const serviceSelectionLabelStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 'bold',
  marginBottom: '8px',
  color: '#333'
};

const noticeStyle: React.CSSProperties = {
  fontSize: '12px',
  lineHeight: '18px',
  color: '#333',
  marginTop: '12px',
  marginBottom: '12px'
};

const getTicketingQrServiceLabel = (ticketingQrService: number): string => {
  switch (ticketingQrService) {
    case PICKUP_SERVICE_EKINET:
      return 'JR東日本';
    case PICKUP_SERVICE_E5489:
      return 'JR西日本';
    default:
      return '';
  }
};

const getTicketingMethodFromService = (pickupService: number): string => {
  switch (pickupService) {
    case PICKUP_SERVICE_EKINET:
      return 'qr_east';
    case PICKUP_SERVICE_E5489:
      return 'qr_west';
    default:
      return '';
  }
};

const findRailwayAreaForService = (
  station: StationJson | null | undefined,
  serviceId: number
): RailwayAreaJson | undefined => {
  return station?.prefecture?.railway_areas?.find(area => Number(area.service_id) === serviceId);
};

const getShinkansenSegments = (item: ReservingTripItem) => {
  if (!(item.element instanceof Transit)) {
    return [];
  }
  return item.element.segments.filter(segment => segment.type === 'shin' || segment.type === 'express');
};

const getSegmentDepartureName = (item: ReservingTripItem): string | null => {
  const segment = getShinkansenSegments(item)[0];
  if (!segment) {
    return null;
  }
  return segment.boarding?.name || segment.legs[0]?.from?.name || null;
};

const getSegmentArrivalName = (item: ReservingTripItem): string | null => {
  const segment = getShinkansenSegments(item)[0];
  if (!segment) {
    return null;
  }
  return segment.arrival?.name || segment.legs[segment.legs.length - 1]?.to?.name || null;
};

const PickupStationSection: React.FC<Props> = observer(
  ({ reserveInfo, stations, serviceId, travelerIndex = null, onPickupStationChange }) => {
    const shinkansenItems = reserveInfo.reservingTrip.items.filter(
      (item: ReservingTripItem) => item.shinkansenExists || item.expressExists
    );

    if (shinkansenItems.length === 0) {
      return null;
    }

    const stripParenthesisSuffix = (name: string): string => name.replace(/[（(][^）)]*[）)]$/, '');

    const stationNameCandidates = (stationName: string): string[] => {
      const withoutEki = stationName.replace(/駅$/, '');
      const stripped = stripParenthesisSuffix(withoutEki);
      return Array.from(
        new Set([stationName, withoutEki, `${withoutEki}駅`, stripped, `${stripped}駅`].filter(Boolean))
      );
    };

    const findStationByName = (stationName: string): StationJson | undefined => {
      const nameCandidates = stationNameCandidates(stationName);
      let matched = stations.filter(
        s => nameCandidates.includes(s.station_name) || nameCandidates.includes(s.station_name.replace(/駅$/, ''))
      );
      if (matched.length === 0) {
        return undefined;
      }

      // 「郡山(福島県)」→ 福島県の郡山だけに絞る（奈良県の郡山へフォールバックしない）
      const prefHintMatch = stationName.replace(/駅$/, '').match(/[（(]([^）)]+)[）)]$/);
      if (prefHintMatch) {
        const hint = prefHintMatch[1].replace(/[都道府県]$/, '');
        const byPref = matched.filter(s => {
          const prefName = s.prefecture?.name || '';
          return prefName.includes(hint) || hint.includes(prefName.replace(/[都道府県]$/, ''));
        });
        if (byPref.length === 0) {
          return undefined;
        }
        matched = byPref;
      }

      if (matched.length === 1) {
        return matched[0];
      }

      return matched.find(s => !!findRailwayAreaForService(s, serviceId)) || matched[0];
    };

    const getStationDisplayName = (stationName: string): string => {
      const station = findStationByName(stationName);
      const rawBase = station?.station_name || stationName;
      const baseName = rawBase.endsWith('駅') ? rawBase : `${rawBase}駅`;
      if (station?.prefecture?.railway_areas && station.prefecture.railway_areas.length > 0) {
        const railwayArea = findRailwayAreaForService(station, serviceId);
        const serviceLabel = railwayArea ? getTicketingQrServiceLabel(railwayArea.ticketing_qr_service) : '';
        if (serviceLabel) {
          return `${baseName}（${serviceLabel}）`;
        }
      }
      return baseName;
    };

    const handleStationChange = (
      item: ReservingTripItem,
      value: string,
      stationName: string | null,
      manualService: number | null = null
    ) => {
      const searchQueryItemId = item.element instanceof Transit ? item.element.searchQueryItemId : null;
      if (!searchQueryItemId) {
        return;
      }

      const station = stationName ? findStationByName(stationName) : null;
      let ticketingMethod: string | null = null;
      let pickupService: number | null = manualService;

      if (station?.prefecture?.railway_areas && station.prefecture.railway_areas.length > 0) {
        const railwayArea = findRailwayAreaForService(station, serviceId);
        if (railwayArea) {
          pickupService = railwayArea.ticketing_qr_service;
          ticketingMethod = getTicketingMethodFromService(railwayArea.ticketing_qr_service);
        }
      } else if (manualService !== null) {
        ticketingMethod = getTicketingMethodFromService(manualService);
      }

      const resolvedStationName = station
        ? station.station_name.endsWith('駅')
          ? station.station_name
          : `${station.station_name}駅`
        : stationName
        ? stationName.endsWith('駅')
          ? stationName
          : `${stationName}駅`
        : null;

      onPickupStationChange({
        searchQueryItemId,
        travelerIndex,
        selectionValue: value,
        pickupStation: value === 'other' ? 'その他' : resolvedStationName || '',
        pickupStationId: station?.id || null,
        pickupService,
        ticketingMethod
      });
    };

    const getDepartureStations = (item: ReservingTripItem, itemIndex: number): string[] => {
      const departureStations: string[] = [];
      const currentDeparture = getSegmentDepartureName(item);
      if (currentDeparture) {
        departureStations.push(currentDeparture);
      }

      if (itemIndex > 0) {
        for (let i = 0; i < itemIndex; i++) {
          const prevDeparture = getSegmentDepartureName(shinkansenItems[i]);
          if (prevDeparture && !departureStations.includes(prevDeparture)) {
            departureStations.push(prevDeparture);
          }
        }
      }

      return departureStations;
    };

    const testIdSuffix = travelerIndex === undefined || travelerIndex === null ? '' : `-traveler-${travelerIndex}`;

    return (
      <div style={sectionStyle} data-testid={`reserve-confirm-input-pickup-station-section${testIdSuffix}`}>
        <div style={sectionTitleStyle}>
          {travelerIndex === undefined || travelerIndex === null
            ? 'チケット受け取り希望場所について'
            : 'チケット受け取り希望場所'}
        </div>
        {shinkansenItems.map((item: ReservingTripItem, index: number) => {
          const searchQueryItemId = item.element instanceof Transit ? item.element.searchQueryItemId : null;
          if (!searchQueryItemId) {
            return null;
          }

          const departureStations = getDepartureStations(item, index);
          const selectedValue = reserveInfo.getPickupStationSelection(searchQueryItemId, travelerIndex);
          const isOtherSelected = selectedValue === 'other';
          const fromName = getSegmentDepartureName(item);
          const toName = getSegmentArrivalName(item);

          return (
            <div key={`${searchQueryItemId}-${travelerIndex ?? 'all'}`} style={itemSectionStyle}>
              {fromName && toName && (
                <div style={routeLabelStyle}>
                  {fromName} → {toName}
                </div>
              )}
              <RadioGroup
                layout="horizontal"
                value={selectedValue || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  if (value === 'other') {
                    handleStationChange(item, value, null);
                  } else {
                    handleStationChange(item, value, value);
                  }
                }}
              >
                {departureStations.map(stationName => (
                  <Radio
                    key={stationName}
                    value={stationName}
                    radioInputProps={{
                      'data-testid': `reserve-confirm-input-pickup-station-${searchQueryItemId}${testIdSuffix}-${stationName}`
                    }}
                  >
                    {getStationDisplayName(stationName)}
                  </Radio>
                ))}
                <Radio
                  value="other"
                  radioInputProps={{
                    'data-testid': `reserve-confirm-input-pickup-station-${searchQueryItemId}${testIdSuffix}-other`
                  }}
                >
                  その他
                </Radio>
              </RadioGroup>
              {isOtherSelected && (
                <div style={serviceSelectionSectionStyle}>
                  <div style={serviceSelectionLabelStyle}>発券サービスを選択してください</div>
                  <RadioGroup
                    layout="horizontal"
                    value={
                      reserveInfo.getPickupServiceSelection(searchQueryItemId, travelerIndex)?.toString() || ''
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const service = parseInt(e.target.value, 10);
                      handleStationChange(item, 'other', null, service);
                    }}
                  >
                    <Radio
                      value={PICKUP_SERVICE_EKINET.toString()}
                      radioInputProps={{
                        'data-testid': `reserve-confirm-input-pickup-service-${searchQueryItemId}${testIdSuffix}-ekinet`
                      }}
                    >
                      JR東日本（えきねっと）
                    </Radio>
                    <Radio
                      value={PICKUP_SERVICE_E5489.toString()}
                      radioInputProps={{
                        'data-testid': `reserve-confirm-input-pickup-service-${searchQueryItemId}${testIdSuffix}-e5489`
                      }}
                    >
                      JR西日本（e5489）
                    </Radio>
                  </RadioGroup>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }
);

export const PickupStationNotice: React.FC<PickupStationNoticeProps> = ({ perTraveler = false }) => (
  <div style={noticeStyle} data-testid="reserve-confirm-input-pickup-station-notice">
    <div>【注意事項】</div>
    <ul style={{ margin: '4px 0 0', paddingLeft: '18px' }}>
      <li>指定した受け取り駅が所属するJRサービスであればお受け取り可能です。</li>
      <li>選択した受け取り場所は、ご指定後に変更することはできません。</li>
      <li>
        一部のJR駅では発券端末がなく、チケットを受け取れない場合がございます。選択された駅に発券端末があるかはこちらからご確認できます。（
        <Link href={JR_EAST_URL} isExternal target="_blank">
          JR東日本エリア
        </Link>
        、
        <Link href={JR_WEST_URL} isExternal target="_blank">
          JR西日本エリア
        </Link>
        ）
      </li>
      {!perTraveler && (
        <li>
          同行者分のチケットをまとめて予約する場合、チケットの仕様上、全席分まとめての受け取りになります。複数名で異なるエリアでの受け取りをご希望の場合は「個別にチケット選ぶ」をご利用ください。
        </li>
      )}
    </ul>
  </div>
);

export default PickupStationSection;
